import { Loader, Modal } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Endpoints } from '../../../../../apis';
import { Button } from '../../../../../components/Atoms';
import { useAttachSecurityGroupMutate } from '../../../../../hooks/CloudComputeHooks/useAttachSecurityGroupMutate';

interface AttachSecurityGroupModalProps {
    opened: boolean;
    onClose: () => void;
    instanceId: number;
    attachedIds: string[];
}

export default function AttachSecurityGroupModal({
    opened,
    onClose,
    instanceId,
    attachedIds,
}: AttachSecurityGroupModalProps) {
    const { t } = useTranslation('cloudComputeInstance');
    const attachMutation = useAttachSecurityGroupMutate();
    const [selected, setSelected] = useState<string | null>(null);

    const groupsQuery = useInfiniteQuery({
        queryKey: ['api.v1.firewalls.security-groups.infinite'],
        queryFn: ({ pageParam }) =>
            Endpoints.v1.Firewalls.SecurityGroups.listSecurityGroups({
                page: pageParam,
                limit: 20,
            }),
        initialPageParam: 1,
        getNextPageParam: (last) => (last.page < last.total_pages ? last.page + 1 : undefined),
        enabled: opened,
    });

    useEffect(() => {
        if (!opened) setSelected(null);
    }, [opened]);

    const options = useMemo(() => {
        const rows = groupsQuery.data?.pages.flatMap((p) => p.rows) ?? [];
        return rows.filter((g) => !attachedIds.includes(g.id));
    }, [groupsQuery.data, attachedIds]);

    const isLoading = groupsQuery.isPending;
    const noOptions = !isLoading && options.length === 0 && !groupsQuery.hasNextPage;
    const isSaving = attachMutation.isPending;

    useEffect(() => {
        if (!opened) return;
        if (
            options.length === 0 &&
            groupsQuery.hasNextPage &&
            !groupsQuery.isFetchingNextPage &&
            !groupsQuery.isPending
        ) {
            groupsQuery.fetchNextPage();
        }
    }, [
        opened,
        options.length,
        groupsQuery.hasNextPage,
        groupsQuery.isFetchingNextPage,
        groupsQuery.isPending,
    ]);

    const handleSave = () => {
        if (!selected) return;
        attachMutation.mutate(
            { id: instanceId, security_group_id: selected },
            { onSuccess: () => onClose() }
        );
    };

    return (
        <Modal
            size="sm"
            opened={opened}
            onClose={onClose}
            withCloseButton={false}
            overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
            }}
            transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
            centered
        >
            <div className="flex flex-col justify-start items-start gap-4">
                <div className="flex flex-col justify-start items-start">
                    <Modal.Title>
                        <span className="text-base font-bold">
                            {t('firewall.attachGroupTitle')}
                        </span>
                    </Modal.Title>
                    <p className="text-sm text-fg/68">{t('firewall.attachGroupDescription')}</p>
                </div>
                <div className="w-full flex flex-col justify-start items-start gap-6">
                    {isLoading ? (
                        <div className="w-full flex justify-center py-4">
                            <Loader size={18} color="var(--fg)" />
                        </div>
                    ) : noOptions ? (
                        <p className="w-full text-sm text-fg/68 py-2">
                            {t('firewall.attachGroupNoOptions')}
                        </p>
                    ) : (
                        <div className="w-full flex flex-col gap-2 max-h-[320px] overflow-y-auto pr-1">
                            {options.map((sg) => {
                                const isSelected = selected === sg.id;
                                return (
                                    <button
                                        key={sg.id}
                                        type="button"
                                        onClick={() => setSelected(sg.id)}
                                        disabled={isSaving}
                                        className={[
                                            'w-full text-left rounded-md border p-3 transition-colors',
                                            isSelected
                                                ? 'border-[#3b82f6] bg-[#3b82f614]'
                                                : 'border-line bg-surface-2 hover:bg-surface-1',
                                            'disabled:opacity-60 disabled:cursor-not-allowed',
                                        ].join(' ')}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex flex-col min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-medium text-fg truncate">
                                                        {sg.name}
                                                    </span>
                                                    <span className="text-xs text-fg/68 font-mono shrink-0">
                                                        {t('firewall.sgInOutCount', {
                                                            inbound: sg.in_rule_count,
                                                            outbound: sg.out_rule_count,
                                                        })}
                                                    </span>
                                                </div>
                                                {sg.description && (
                                                    <div className="text-xs text-fg/68 mt-1 truncate">
                                                        {sg.description}
                                                    </div>
                                                )}
                                            </div>
                                            <span
                                                className={[
                                                    'mt-0.5 inline-flex items-center justify-center shrink-0 w-4 h-4 rounded-full border',
                                                    isSelected
                                                        ? 'bg-[#3b82f6] border-[#3b82f6]'
                                                        : 'border-line-strong',
                                                ].join(' ')}
                                            >
                                                {isSelected && (
                                                    <IconCheck size={12} color="white" />
                                                )}
                                            </span>
                                        </div>
                                    </button>
                                );
                            })}
                            {groupsQuery.hasNextPage && (
                                <button
                                    type="button"
                                    onClick={() => groupsQuery.fetchNextPage()}
                                    disabled={groupsQuery.isFetchingNextPage || isSaving}
                                    className="w-full text-center text-sm text-blue-400 hover:text-blue-300 light:text-blue-700 light:hover:text-blue-800 py-2 disabled:opacity-60"
                                >
                                    {groupsQuery.isFetchingNextPage
                                        ? t('firewall.loading')
                                        : t('firewall.loadMore')}
                                </button>
                            )}
                        </div>
                    )}
                    <div className="w-full flex flex-col justify-start items-start gap-2.5">
                        <Button
                            width="100%"
                            onClick={handleSave}
                            disabled={!selected || isSaving || noOptions}
                        >
                            {isSaving ? (
                                <div className="flex items-center gap-2">
                                    <Loader size={16} color="white" />
                                    <span>{t('firewall.attaching')}</span>
                                </div>
                            ) : (
                                t('firewall.attachGroupButton')
                            )}
                        </Button>
                        <Button
                            width="100%"
                            variant="secondary"
                            onClick={onClose}
                            disabled={isSaving}
                        >
                            {t('firewall.cancel')}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
